import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import type { RootState } from "app/store";

import { lastTenDigits } from "./phone-match.util";

export type OutboundCallProgressEvent = "initiated" | "answered" | "ended";

// One websocket `voice.outbound_call_status` frame (dialstack/notifications.py):
// how a click-to-call the user is taking on their desk phone is going. The
// softphone SDK only sees legs the browser carries, so this is the widget's
// only view of a call that lives on the handset (ENG-2183).
export type OutboundCallProgress = {
  event: OutboundCallProgressEvent;
  // Backend Call.id — may change once between frames when the webhook folds
  // the click row into an out-of-order placeholder, so nothing keys on it.
  callId: number;
  toNumber: string;
  fromNumber: string;
  patientId: number | null;
  callStatus: string | null;
  answeredAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  receivedAt: number;
};

type OutboundCallProgressState = {
  // Keyed by the destination's last ten digits — what the widget knows about
  // the dial it placed (the click row's number as typed, E.164 on the wire).
  byNumber: Record<string, OutboundCallProgress>;
};

const EVENT_ORDER: Record<OutboundCallProgressEvent, number> = {
  initiated: 0,
  answered: 1,
  ended: 2
};

// Progress outlives the call by this much; after that a stale "ended" cannot
// be mistaken for a fresh dial to the same number.
export const OUTBOUND_CALL_PROGRESS_TTL_MS = 10 * 60_000;

const initialState: OutboundCallProgressState = { byNumber: {} };

export const outboundCallProgressSlice = createSlice({
  name: "outboundCallProgress",
  initialState,
  reducers: {
    // Webhooks are delivered at least once and in no guaranteed order: a
    // re-sent `initiated` must not roll an answered call back to waiting.
    progressReceived: (state, action: PayloadAction<OutboundCallProgress>) => {
      const key = lastTenDigits(action.payload.toNumber);
      if (!key) return;
      const existing = state.byNumber[key];
      if (
        existing &&
        existing.callId === action.payload.callId &&
        EVENT_ORDER[existing.event] > EVENT_ORDER[action.payload.event]
      )
        return;
      state.byNumber[key] = action.payload;
    },
    progressCleared: (state, action: PayloadAction<string>) => {
      delete state.byNumber[lastTenDigits(action.payload)];
    }
  }
});

export const { progressReceived, progressCleared } =
  outboundCallProgressSlice.actions;

// The progress of a dial placed at `since` (ms) to `number`, if any has
// arrived for it: frames from an earlier call to the same number are not it.
export const selectOutboundCallProgress = (
  state: RootState,
  number: string,
  since: number
): OutboundCallProgress | undefined => {
  const progress = state.outboundCallProgress.byNumber[lastTenDigits(number)];
  if (!progress || progress.receivedAt < since) return undefined;
  if (Date.now() - progress.receivedAt > OUTBOUND_CALL_PROGRESS_TTL_MS)
    return undefined;
  return progress;
};

export default outboundCallProgressSlice.reducer;
